import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MembershipTile } from "../../components/Tile/MembershipTile";
import InputBox from "../../components/Input/Input";
import Slab from "../../components/Slab/Slab";
import useAuthService from "../../auth/AuthService";
import { GetMemberships } from "../../services/memberships.service";
import { handleDate } from "../../utils/formatdate";
import Button from "../../components/Button/Button";
import "./MembershipPage.css";
import NullPageComponent from "../../components/Tile/NullPageComponent";

// Define User interface with all required properties
interface IProduct {
  product_id: string;
  public_id: string;
  product_name: string;
  price: number;
  image_url: string;
  purchase_date: string;
  expire_date: string;
}

const Memberships: React.FC = () => {
  const { isAuthenticated, getToken } = useAuthService();
  const navigate = useNavigate();

  const [state, setState] = useState({
    isLoading: false,
    products: [] as IProduct[],
    allProducts: [] as IProduct[],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isAuthenticated) {
          handleToggle("isLoading", true);
          const response = await GetMemberships(getToken);
          if (response.data.status_code === 200 && response.error === null) {
            setState((prev) => ({
              ...prev,
              products: response.data.data || [],
              allProducts: response.data.data || [],
            }));
          }
        }
      } catch (error) {
        console.error("Error in fetching memberships.", error);
      } finally {
        handleToggle("isLoading", false);
      }
    };

    fetchData();
    //eslint-disable-next-line
  }, [isAuthenticated]);

  const handleClick = (id: string, internal_id: string) => {
    navigate(`/${id}`, { state: { id: internal_id } });
  };

  const handleToggle = (key: keyof typeof state, value: boolean) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    if (state.allProducts) {
      setState((prev) => ({
        ...prev,
        products: searchValue
          ? prev.allProducts.filter((product: IProduct) =>
              product.product_name.toLowerCase().includes(searchValue)
            )
          : prev.allProducts,
      }));
    }
  };

  return (
    <>
      {state.isLoading ? (
        <div className="loader">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          <div className="container membership-page px-2">
            <div className="membership-header">
              <div className="title">Memberships</div>

              {/* Conditionally render the search bar */}
              {state.allProducts.length > 0 && (
                <div className="search-bar">
                  <InputBox
                    type="search"
                    onChange={handleSearchChange}
                    className="ps-3 me-2 search-box"
                    placeholder="Search"
                  />
                </div>
              )}
            </div>

            {/* Render message when there are no memberships */}
            {state.allProducts && state.allProducts.length === 0 ? (
              <NullPageComponent
                heading1={"You don’t have any Memberships yet!"}
                heading2={"Browse available memberships and become Fan1 today!"}
                button1={
                  <>
                    <Button
                      label="Go to Available now!"
                      className="btn"
                      onClick={() => navigate("/available")}
                      variant="dark"
                    />
                  </>
                }
              />
            ) : (
              <div className="assets-box-custom shadow-box">
                {state.allProducts != null &&
                  state.products.map((product, index) => (
                    <div key={index}>
                      <MembershipTile
                        name={product.product_name}
                        date={handleDate(product.expire_date as string)}
                        src={product.image_url}
                        onClick={() =>
                          handleClick(product.public_id, product.product_id)
                        }
                        className="mx-auto"
                      />
                    </div>
                  ))}
              </div>
            )}

            {/* Display products if available */}
            <div className="tile-view">
              {state.allProducts != null &&
                state.products.map((product, index) => (
                  <div key={index}>
                    <Slab
                      content={
                        <div className="d-flex w-100">
                          <img
                            src={product.image_url}
                            alt={product.product_name}
                            className="image"
                          />
                          <div className="content">
                            <div
                              onClick={() => {
                                handleClick(
                                  product.public_id,
                                  product.product_id
                                );
                              }}
                              className="purple-color d-flex align-items-center text-start ms-3 line-24 font-20 font-500 cursor-pointer w-75"
                            >
                              {product.product_name}
                            </div>
                            <div className="my-auto text-start font-inter-500-16 ms-5 w-50">
                              <div className="font-inter-500-14">Expires</div>
                              <div className="extra-light-purple font-inter-400-14">
                                {handleDate(product.expire_date)}
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                      className="py-2"
                    />
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Memberships;
